<!--
  The editor for follow intents

  Props:
  - value: the value
      ex: { // action: null (stop the intent)
            'condition': '@value > 0', // empty for default condition
          }
      ex: { // action: 'follow' (call an intent)
            'condition': '@value > 0', // empty for default condition
            'skill': 'skillid', // empty for this skill
            'intent': 'intentid', // empty to stop the skill
            'follow': false, // to follow all local variables
            'variables': { // local variables to pass to the intent
              'var1': '123',
              'var2': '@value'
            },
            callback: { // null for no callback
              'intent': 'intentid', // empty for the skill default callback
              'follow': false, // to follow all local variables
              'variables': { // local variables to pass to the callback
                'var1': '123',
                'var2': '@value'
              },
            },
          }
      ex: { // action: 'exit' (exit the skill)
            'condition': '@value > 0', // empty for default condition
            'exit': 'exit', // to exit the skill
          }
      ex: { // action: 'callback' (call the callback)
            'condition': '@value > 0', // empty for default condition
            'exit': 'callback', // to exit the skill
            'follow': false, // to follow all local variables
            'variables': { // local variables to pass to the intent
              'var1': '123',
              'var2': '@value'
            },
          }
  - other: a value to compare with
  - prop: the prop prefix to use for el-form-item
  - last: if it is the last item
      the condition is then not required
  - active: if it should open its dropdown
  - specialSkills: the available special skills
  - skillOptions: the available public skills
  - intentOptions: the available intents, by skill
  - localVariables: for the code-input,
  - contextVariables: for the code-input,
  - variableSuggestions: for the code-input,
-->

<template>
  <comparator-filter>
    <template v-slot="{ compare }">
      <!-- orderable -->
      <orderable-box
        :disabled="compare || last && !action"
        v-bind="$attrs"
        v-on="$listeners">
        <!-- the title, as a input for the condition -->
        <comparable slot="title"
          prop="value.condition" default="" :ignore="computedIgnore">
          <template v-slot="{ view, compare, status }">
            <el-form-item-code-input :class="'comparable-' + status"
              v-model="copy.condition"
              :prop="view && prop && prop + '.condition'"
              :required="!last"
              field="condition"
              :show-message="true"
              :varstatus="varstatus"
              placeholder="default condition"
              :disabled="compare"
              clearable></el-form-item-code-input>
          </template>
        </comparable>
        <!-- radios for the type of follow -->
        <p @click.stop>
          <comparable field="action-null" diff :ignore="computedIgnore"
            :prop="({value: v}, b) => getAction(v, b)" :expected="null">
            <template v-slot="{ status, compare, value }">
              <el-radio :class="'comparable-' + status"
                v-model="action" :label="null" :disabled="compare"
                >Stop the intent
                <tooltip-icon placement="top">
                  Stop and wait for further user input
                </tooltip-icon>
              </el-radio>
            </template>
          </comparable>
          <comparable field="action-intent" diff :ignore="computedIgnore"
            :prop="({value: v}, b) => getAction(v, b)" expected="intent">
            <template v-slot="{ status, compare, value }">
              <el-radio :class="'comparable-' + status"
                v-model="action" label="intent" :disabled="compare"
                >Call an intent
                <tooltip-icon placement="top">
                  Call another intent to continue processing.<br/>
                  Useful for conditional programming.
                </tooltip-icon>
              </el-radio>
            </template>
          </comparable>
          <comparable field="action-exit" diff :ignore="computedIgnore"
            :prop="({value: v}, b) => getAction(v, b)" expected="exit">
            <template v-slot="{ status, compare, value }">
              <el-radio :class="'comparable-' + status"
                v-model="action" label="exit" :disabled="compare"
                >Exit the skill
                <tooltip-icon placement="top">
                  Finishes the skill, no further user input is expected
                </tooltip-icon>
              </el-radio>
            </template>
          </comparable>
          <comparable field="action-callback" diff :ignore="computedIgnore"
            :prop="({value: v}, b) => getAction(v, b)" expected="callback">
            <template v-slot="{ status, compare, value }">
              <el-radio :class="'comparable-' + status"
                v-model="action" label="callback" :disabled="compare"
                >Call the callback
                <tooltip-icon placement="top"
                  :type="action === 'callback' ? 'warning' : 'info'">
                  <span class="warning-text">
                    <i class="el-icon-warning"></i>[Advanced usage]<br/>
                  </span>
                  Calls the callback intent.<br/>
                  Useful for fuction-like intent.
                </tooltip-icon>
              </el-radio>
            </template>
          </comparable>
        </p>
        <!-- <follow-intent-sub-editor> with comparator over the action -->
        <comparator :view="value" :compare="other"
          :prop="getAction"
          :intent.sync="intent"
          :follow.sync="follow"
          :variables="variables"
          @update:variables="handleVariables"
          :callback.sync="callback"
          :callback-copy.sync="callbackCopy"
          :callback-variables="callbackVariables"
          @update:callback-variables="handleCallbackVariables"
          :speech.sync="speech"
          :active="active"
          @activate="v => $emit('activate', v)">
          <template v-slot="{ value, other, view, compare, attrs, listeners }">
            <follow-intent-sub-editor
              :value="value"
              :other="other"
              :compare="compare"
              :action="getAction(value, view)"

              :prop="prop"
              :special-skills="specialSkills"
              :skill-options="skillOptions"
              :intent-options="intentOptions"
              :local-variables="localVariables"
              :context-variables="contextVariables"
              :variable-suggestions="variableSuggestions"

              v-bind="attrs"
              v-on="listeners">
            </follow-intent-sub-editor>
          </template>
        </comparator>
      </orderable-box>
    </template>
  </comparator-filter>
</template>

<script>
import OrderableBox from '../orderable/OrderableBox.vue'
import ElFormItemCodeInput from '../element/ElFormItemCodeInput.vue'
import FollowIntentSubEditor from './FollowIntentSubEditor.vue'

const VAR_STATUS = {
  local: 'readonly',
  context: 'readonly',
  contexts: 'readonly',
}

export default {
  name: 'FollowIntentEditor',

  components: {
    OrderableBox,
    ElFormItemCodeInput,
    FollowIntentSubEditor,
  },

  props: {
    value: Object,
    other: Object, // the value to compare with
    prop: String, // the prop prefix for all el-form-item
    last: Boolean, // if it is the last item
    active: Boolean, // if it should open its dropdown
    specialSkills: Object, // the available special skills
    skillOptions: Array, // the available public skills
    intentOptions: Object, // the available intents, by skill
    // for the code-input
    localVariables: Array,
    contextVariables: Array,
    variableSuggestions: Object,
  },

  data () {
    return {
      //  an editable copy of the value
      copy: {
        condition: '',
        exit: null,
        skill: null,
        intent: null,
        follow: false,
        variables: null,
        callback: null,
        speech: null,
      },
      action: null, // the type of action to do
      // the intent chosen to follow
      intent: {
        skill: null,
        intent: null,
      },
      follow: false, // if should follow all local variables
      variables: {}, // the variablles to pass to the intent
      callback: false, // if should pass a callback
      // the callback object
      callbackCopy: {
        intent: null,
        follow: false,
        variables: {},
      },
      callbackVariables: {}, // the variables of the callback
      // the speech object to pass
      speech: {
        delaytime: undefined,
        volume: undefined,
      },
      // for the condition's code-input
      varstatus: VAR_STATUS,
    }
  },

  computed: {
    /* if the value cannot have intent */
    intentDisabled () { return this.action !== 'intent' },
    /* if the value should contain the variables field */
    hasVariables () {
      return this.action === 'callback' || this.action === 'intent'
    },
    /* if the value cannot have callback */
    callbackDisabled () { return this.action !== 'intent' },
    /* if the value should contain the callback field */
    hasCallback () {
      return !this.callbackDisabled && this.callback
    },
    /* if the skill is a olli. special skill */
    hasOlliSkill () {
      return this.copy.skill &&
      this.intent.skill.substr(0, 'olli.'.length) === 'olli.'
    },
    /* if the iten should be ignored (last and no value) */
    computedIgnore () {
      return this.last && !this.copy.condition && !this.action
    },
    /* the speech object */
    computedSpeech () {
      if (this.action !== null) return null
      let s = this.speech, out = null
      if (typeof s.delaytime === 'number') {
        out = out || {}
        out.delaytime = s.delaytime
      }
      if (typeof s.volume === 'number') {
        out = out || {}
        out.volume = s.volume
      }
      return out
    },
  },
  /* init the value */
  created () { this.updateValue() },
  watch: {
    /* value has changed */
    value () { this.updateValue() },
    /* copy has changed, emit the value */
    copy: {
      handler () { this.valueUpdated() },
      deep: true,
    },
    /* action has changed, update copy.exit */
    action (val) {
      if (val === 'exit') {
        this.copy.exit = 'exit'
      } else if (val === 'callback') {
        this.copy.exit = 'callback'
      } else {
        this.copy.exit = null
      }
    },
    /* add or remove the intent fields from copy */
    intentDisabled (val) {
      if (val) {
        this.copy.skill = null
        this.copy.intent = null
      } else {
        this.copy.skill = this.intent.skill
        this.copy.intent = this.intent.intent
      }
    },
    /* add or remove the variables field from copy */
    hasVariables (val) {
      if (val) {
        this.copy.variables = this.variables
        this.copy.follow = this.hasOlliSkill ? null : this.follow
      } else {
        this.copy.variables = null
        this.copy.follow = null
      }
    },
    /* add or remove the callback field from copy */
    hasCallback (val) {
      if (val) this.copy.callback = this.callbackCopy
      else this.copy.callback = null
    },
    /* on skill change, clear intent and set callback if public */
    'intent.skill' (val) {
      val = val || null
      if (val !== (this.copy.skill || null) ||
        this.intent.intent !== this.copy.intent) {
        this.intent.intent = null
        if (val) this.callback = true
        else this.callback = false
      }
      if (this.intentDisabled) return
      this.copy.skill = val || null
    },
    /* on intent change, transfer it to copy */
    'intent.intent' (val) {
      if (this.intentDisabled) return
      this.copy.intent = val || null
    },
    /* on follow change, transfer it to copy */
    follow (val) {
      if (!this.hasVariables || this.hasOlliSkill) return
      this.copy.follow = val
    },
    /* remove or not follow depending on olli skill */
    hasOlliSkill (val) {
      if (val) this.copy.follow = null
      else this.copy.follow = this.hasVariables ? null : this.follow
    },
    /* updpate the speech field */
    computedSpeech (val) { this.copy.speech = val },
  },

  methods: {
    /* variables have changed */
    handleVariables (val) {
      this.variables = val
      if (this.hasVariables) this.copy.variables = val
    },
    /* callback variables have changed */
    handleCallbackVariables (val) {
      this.callbackVariables = val
      this.callbackCopy.variables = val
    },
    /* value have changed */
    updateValue () {
      let value = this.value || {}
      let copy = this.copy
      // check if value and copy are different
      let same = (value.condition || '') === (copy.condition || '') &&
        (value.exit || null) === (copy.exit || null) &&
        (value.skill || null) === (copy.skill || null) &&
        (value.intent || null) === (copy.intent || null) &&
        !value.follow === !copy.follow &&
        !value.callback === !copy.callback &&
        !value.speech === !copy.speech
      // check if value.speech and copy.speech are different
      if (same && value.speech) {
        let delaytime = typeof value.speech.delaytime === 'number'
        let volume = typeof value.speech.volume === 'number'
        same = delaytime === (typeof copy.speech.delaytime === 'number') &&
          volume === (typeof copy.speech.volume === 'number')
        if (same && delaytime) { same = value.speech.delaytime === copy.speech.delaytime }
        if (same && volume) { same = value.speech.volume === copy.speech.volume }
      }
      // check if value.callback and copy.callback are different
      if (same && value.callback) {
        let vcallback = value.callback, ccallback = copy.callback
        same = (vcallback.skill || null) === (ccallback.skill || null) &&
          (vcallback.intent || null) === (ccallback.intent || null) &&
          !vcallback.follow === !ccallback.follow
      }
      // check if value.variables and copy.variables are different
      let hasVariables = value.exit === 'callback' || value.skill || value.intent
      if (!same || hasVariables) {
        this.variables = value.variables || {}
        this.follow = !!value.follow
      }
      // check if value.callback.variables and copy.callback.variables are different
      let hasCallback = (value.skill || value.intent) && value.callback
      if (!same || hasCallback) {
        this.callbackVariables = (value.callback || {}).variables || {}
      }
      // they are different, update copy and all the other fields
      if (!same) {
        this.action = this.getAction(value)
        this.intent = {
          skill: value.skill || null,
          intent: value.intent || null,
        }
        let vcallback = value.callback || {}
        this.callbackCopy = {
          skill: vcallback.skill || null,
          intent: vcallback.intent || null,
          follow: !!vcallback.follow,
          variables: this.callbackVariables,
        }
        this.callback = !!value.callback
        this.speech = {
          delaytime: value.speech && typeof value.speech.delaytime ===
            'number' ? value.speech.delaytime : undefined,
          volume: value.speech && typeof value.speech.volume ===
            'number' ? value.speech.volume : undefined,
        }
        this.copy = {
          condition: value.condition || '',
          exit: value.exit || null,
          skill: value.skill || null,
          intent: value.intent || null,
          follow: hasVariables ? this.follow : null,
          variables: hasVariables ? this.variables : null,
          callback: this.callback ? this.callbackCopy : null,
          speech: value.speech ? { ...value.speech } : null,
        }
      }
    },
    /* copy has changed, emit it */
    valueUpdated () {
      this.$emit('input', JSON.parse(JSON.stringify(this.copy)))
    },
    /* computes the action depending on the value */
    getAction (value, view) {
      if (view) return this.action
      return value.exit ? value.exit
        : value.skill || value.intent ? 'intent' : null
    },
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  .follow-intent-editor__prefix {
    color: $--color-text-placeholder;
  }
</style>
